/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, {useEffect, useState} from 'react';
import {
    Routes,
    Route,
    Navigate,
    useNavigate, useLocation
} from 'react-router-dom';

import Base from './common/layout/Base';
import BasePage from './common/layout/BasePage';

import LoginContainer from './common/login/LoginContainer.jsx';
import RecoverContainer from './common/recover/RecoverContainer.jsx';
import ResetContainer from './common/reset/ResetContainer.jsx';

import AdminRouter from './admin/AdminRouter.jsx';
import AdminMenu from './admin/AdminMenu';
import OrganizationRouter from './organization/OrganizationRouter.jsx';
import OrganizationMenu from './organization/OrganizationMenu';

import {useDispatch, useSelector} from "react-redux";
import {getAccount} from "./common/account/accountSlice";
import * as cache from "./common/Cache";
import config from "./config";
import {isOwner} from "./common/account/AccountService";
import AccountConfirmContainer from "./common/account-confirm/AccountConfirmContainer";

// List of routes that uses the page layout
// listed here to Routes between layouts
// depending on the current pathname
const publicPages = [
    '/login',
    '/recover',
    '/reset',
    '/confirm',
];

// Pertains to animation / transition groups
// const timeout = {enter: 500, exit: 500};
// Animations supported
//      'rag-fadeIn'
//      'rag-fadeInRight'
//      'rag-fadeInLeft'
// const animationName = 'rag-fadeIn';

const AppRouter = () => {
    const location = useLocation();

    const accountDataState = useSelector(state => state.account);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Debug logging removed - uncomment if needed for troubleshooting
    // console.log('AppRouter: Current state', { 
    //     pathname: location.pathname,
    //     authenticated: accountDataState.authenticated,
    //     roleMapping: accountDataState.roleMapping,
    //     initialized: accountDataState.initialized,
    //     account: accountDataState.account ? 'present' : 'null',
    //     accountRoles: accountDataState.account?.roles
    // });

    const search = location.search;
    const currentPathname = search ? window.location.pathname + search : window.location.pathname;

    const [organizationMenu, setOrganizationMenu] = useState([]);

    useEffect(() => {
        const orgMenu = [...OrganizationMenu];

        if (isOwner()) {
            orgMenu.push({
                name: 'MANAGE',
                path: '/org/manage',
                subMenu: [
                    // {
                    //     name: 'Integrations',
                    //     path: '/org/manage/integrations'
                    // },
                    {
                        name: 'Team',
                        path: '/org/manage/team'
                    }
                ]
            });
        }

        setOrganizationMenu([...orgMenu]);
    }, [accountDataState.account?.id]);

    useEffect(() => {
        dispatch(getAccount());
        const account = cache.get(config.constants.CACHE_KEY_ACCOUNT);

        if (currentPathname !== '/' && account) {
            navigate(currentPathname);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Additional effect to handle authentication state changes
    useEffect(() => {
        if (accountDataState.authenticated) {
            // If we're on the login page and user is authenticated, redirect based on role
            if (location.pathname === '/login') {
                if (accountDataState.roleMapping === 'ADMIN') {
                    navigate('/admin/home');
                } else if (accountDataState.roleMapping === 'ORGANIZATION') {
                    navigate('/org/home');
                }
            }
        }
    }, [accountDataState.authenticated, accountDataState.roleMapping, location.pathname, navigate]);

    //const currentKey = location.pathname.split('/')[1] || '/';

    // If Authenticated and I'm a member of an organization
    if (accountDataState.authenticated && accountDataState.roleMapping === 'ORGANIZATION') {
        const showMenu = currentPathname.includes('/org/com/');
        return (
            <Base menu={organizationMenu} showMenu={!showMenu}>
                <div>
                    <Routes>
                        <Route path="/" element={<Navigate to="/org/home" replace />} />
                        <Route path="/*" element={<OrganizationRouter />} />
                    </Routes>
                </div>
                {/*http://reactcommunity.org/react-transition-group/transition-group*/}
                {/*<TransitionGroup>*/}
                {/*    <CSSTransition key={currentKey} timeout={timeout} classNames={animationName} exit={false}>*/}
                {/*        <div>*/}
                {/*            <Route path="/" element={<OrganizationRouterreplace/>} replace/>*/}
                {/*        </div>*/}
                {/*    </CSSTransition>*/}
                {/*</TransitionGroup>*/}
            </Base>
        );
    }

    // If Authenticated and I'm an administrator
    if (accountDataState.authenticated && accountDataState.roleMapping === 'ADMIN') {
        const showMenu = currentPathname.includes('/admin/');
        // Debug: Admin interface rendering
        // console.log('AppRouter: Rendering admin interface', { 
        //     authenticated: accountDataState.authenticated, 
        //     roleMapping: accountDataState.roleMapping,
        //     currentPathname,
        //     showMenu 
        // });
        return (
            <Base menu={AdminMenu} showMenu={showMenu}>
                <div>
                    <Routes>
                        <Route path="/" element={<Navigate to="/admin/home" replace />} />
                        <Route path="/*" element={<AdminRouter />} />
                    </Routes>
                </div>
            </Base>
        );
    }

    // Unauthenticated 'Public' routes
    if (!accountDataState.authenticated && publicPages.indexOf(location.pathname) > -1) {
        return (
            // Page Layout component wrapper
            <BasePage>
                <Routes>
                    <Route path="/login" element={<LoginContainer />} />
                    <Route path="/recover" element={<RecoverContainer />} />
                    <Route path="/reset" element={<ResetContainer />} />
                    <Route path="/confirm" element={<AccountConfirmContainer />} />
                </Routes>
            </BasePage>
        );
    }

    // Fallback for authenticated users with undefined role mapping
    if (accountDataState.authenticated && !accountDataState.roleMapping) {
        // console.log('AppRouter: Authenticated user with undefined role mapping, showing login');
        return (
            <BasePage>
                <Routes>
                    <Route path="/login" element={<LoginContainer />} />
                    <Route path="/recover" element={<RecoverContainer />} />
                    <Route path="/reset" element={<ResetContainer />} />
                    <Route path="/confirm" element={<AccountConfirmContainer />} />
                    <Route path="/*" element={<Navigate to="/login" replace />} />
                </Routes>
            </BasePage>
        );
    }

    // console.log('AppRouter: No matching condition, redirecting to login');
    return (
        <Navigate to="/login" replace />
    );
}

export default AppRouter;
